import { getStoredSessionToken } from "./session-token";
import { getApiBase } from "./api-base";
import { invokeEncryptedApi } from "./e2ee-client";
import { prepareChatUploadFile } from "./prepare-chat-upload";
import { fileToBase64, shouldUseEncryptedChatUpload } from "./chat-upload";
import { clearImpersonation, getImpersonationToken, impersonationHeaders } from "./impersonation";

export { getApiBase };

export class ApiError extends Error {
  status: number;
  retryable: boolean;
  detail?: string;
  /** Set when the broker's own Claude key is blocked and Prime's model needs their OK. */
  claudeBlocked?: { kind: string; until: string | null };

  constructor(
    message: string,
    status: number,
    retryable: boolean,
    detail?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retryable = retryable;
    this.detail = detail;
  }
}

function authHeaders(): Record<string, string> {
  const token = getStoredSessionToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...impersonationHeaders(),
  };
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function payloadError(payload: unknown, status: number): ApiError {
  const err = payload as {
    error?: string;
    errorDetail?: string;
    impersonationEnded?: boolean;
    claudeBlocked?: { kind: string; until: string | null };
  };
  if (err?.impersonationEnded && getImpersonationToken()) {
    // View-as-user expired or was stopped: drop the claim and reload as yourself.
    clearImpersonation();
    window.setTimeout(() => window.location.assign("/settings/admin"), 50);
  }
  const detail =
    typeof err.errorDetail === "string" && err.errorDetail.trim()
      ? err.errorDetail.trim()
      : undefined;
  const apiErr = new ApiError(
    err.error ?? `Request failed (${status})`,
    status,
    isRetryableStatus(status),
    detail,
  );
  if (err?.claudeBlocked) {
    apiErr.claudeBlocked = err.claudeBlocked;
  }
  return apiErr;
}

async function invokeWithRetry(path: string, init?: RequestInit): Promise<{
  status: number;
  payload: unknown;
}> {
  const method = (init?.method ?? "GET").toUpperCase();
  const retries = method === "GET" || method === "HEAD" ? 3 : 1;
  let lastError: unknown;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      return await invokeEncryptedApi(path, init ?? {}, authHeaders());
    } catch (err) {
      lastError = err;
      // Stop / timeout: surface the abort as-is, never retry.
      if (init?.signal?.aborted) {
        throw err;
      }
      if (attempt === retries - 1) {
        throw err instanceof ApiError
          ? err
          : new ApiError(
              err instanceof Error
                ? err.message
                : "Cannot reach the Mortgage Broker Assistant API. Check your connection and refresh.",
              0,
              true,
            );
      }
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 400 * (attempt + 1));
    });
  }
  throw lastError instanceof Error
    ? lastError
    : new ApiError("Cannot reach the Mortgage Broker Assistant API.", 0, true);
}

export async function apiUpload<T>(path: string, formData: FormData, init?: RequestInit): Promise<T> {
  const file = formData.get("file");
  if (!(file instanceof File)) {
    throw new ApiError("file field required", 400, false);
  }
  const target = path.split("?")[0] ?? path;
  if (target === "/attachments" || target.endsWith("/attachments")) {
    return uploadChatFileToStorage<T>(file);
  }
  return apiFetch<T>(path, {
    ...init,
    method: init?.method ?? "POST",
    body: JSON.stringify({
      filename: file.name || "upload",
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
    }),
  });
}

async function uploadChatFileViaJson<T>(prepared: File): Promise<T> {
  const contentBase64 = await fileToBase64(prepared);
  return apiFetch<T>("/attachments", {
    method: "POST",
    body: JSON.stringify({
      filename: prepared.name || "upload",
      mimeType: prepared.type || "application/octet-stream",
      contentBase64,
    }),
  });
}

async function uploadChatFileViaSignedUrl<T>(prepared: File): Promise<T> {
  const ticket = await apiFetch<{
    id: string;
    signedUrl: string;
    token: string;
    mimeType: string;
  }>("/attachments/sign", {
    method: "POST",
    body: JSON.stringify({
      filename: prepared.name || "upload",
      mimeType: prepared.type || "application/octet-stream",
      sizeBytes: prepared.size,
    }),
  });
  const headers: Record<string, string> = {
    "Content-Type":
      ticket.mimeType || prepared.type || "application/octet-stream",
    "x-upsert": "false",
  };
  let put = await fetch(ticket.signedUrl, {
    method: "PUT",
    headers,
    body: prepared,
  });
  if (!put.ok && ticket.token) {
    put = await fetch(ticket.signedUrl, {
      method: "PUT",
      headers: {
        ...headers,
        Authorization: `Bearer ${ticket.token}`,
      },
      body: prepared,
    });
  }
  if (!put.ok) {
    throw new ApiError(
      "Could not store the file. Try a smaller PDF or image under 10 MB.",
      put.status,
      put.status >= 500,
    );
  }
  return apiFetch<T>("/attachments/complete", {
    method: "POST",
    body: JSON.stringify({
      id: ticket.id,
      filename: prepared.name || "upload",
      mimeType: ticket.mimeType || prepared.type || "application/octet-stream",
      sizeBytes: prepared.size,
    }),
  });
}

async function uploadChatFileToStorage<T>(file: File): Promise<T> {
  const prepared = await prepareChatUploadFile(file);
  let status: { atRestEncryption?: boolean } | null = null;
  try {
    status = await apiFetch<{ atRestEncryption?: boolean }>("/chat/status");
  } catch {
    status = null;
  }
  if (shouldUseEncryptedChatUpload({ atRestEncryption: status?.atRestEncryption })) {
    return uploadChatFileViaJson<T>(prepared);
  }
  try {
    return await uploadChatFileViaSignedUrl<T>(prepared);
  } catch (err) {
    const message = err instanceof ApiError ? err.message : "";
    if (shouldUseEncryptedChatUpload({ signErrorMessage: message })) {
      return uploadChatFileViaJson<T>(prepared);
    }
    throw err;
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { status, payload } = await invokeWithRetry(path, init);
  if (status < 200 || status >= 300) {
    throw payloadError(payload, status);
  }
  return payload as T;
}
